import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Zap, 
  ArrowUpRight, 
  ArrowRight, 
  CheckCircle, 
  Monitor, 
  Smartphone, 
  Palette, 
  Copy, 
  Layout, 
  Eye, 
  Keyboard,
  Mail,
  X
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Coluro = () => {
  const heroRef = useRef(null);
  const featuresRef = useRef(null);
  const howItWorksRef = useRef(null);
  const pricingRef = useRef(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const supportInfo = {
    email: 'hello@sqrx.in',
    subject: 'Support Request: Coluro Mac App',
    body: 'Hi Squarex Studios, I need help with Coluro. [Describe your issue here]'
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    document.title = 'Coluro — Pick any color. Instantly. | Squarex Studios';

    const ctx = gsap.context(() => {
      // Hero animations
      gsap.fromTo('.coluro-hero-content > *', 
        { opacity: 0, y: 50 }, 
        { opacity: 1, y: 0, duration: 1, stagger: 0.2, ease: 'power3.out' }
      );
      
      gsap.fromTo('.coluro-hero-visual', 
        { opacity: 0, scale: 0.9, y: 50 }, 
        { opacity: 1, scale: 1, y: 0, duration: 1.5, delay: 0.5, ease: 'power2.out' }
      );

      // Section animations
      const sections = ['.coluro-section', '.feature-card', '.step-item'];
      sections.forEach((selector) => {
        gsap.fromTo(selector, 
          { opacity: 0, y: 40 }, 
          { 
            opacity: 1, 
            y: 0, 
            duration: 0.8, 
            stagger: 0.1,
            ease: 'power2.out',
            scrollTrigger: {
              trigger: selector,
              start: 'top 85%',
            }
          }
        );
      });
    });

    return () => ctx.revert();
  }, []);

  const features = [
    { 
      icon: Eye, 
      title: 'Instant Color Picker', 
      desc: 'Magnified pixel-perfect preview. Grab any color from anywhere on your screen.' 
    },
    { 
      icon: Copy, 
      title: 'Copy in Any Format', 
      desc: 'HEX, RGB, HSL, HSB — copy with one click, paste anywhere into your code or design tool.' 
    },
    { 
      icon: Palette, 
      title: 'Custom Palettes', 
      desc: 'Save and organize colors into named palettes. Build your personal library over time.' 
    },
    { 
      icon: CheckCircle, 
      title: 'Contrast Checker', 
      desc: 'Check foreground/background contrast ratios for WCAG AA and AAA compliance on the fly.' 
    },
    { 
      icon: Monitor, 
      title: 'Menu Bar Access', 
      desc: 'Coluro lives in your menu bar — always one click away, never in your way or cluttering your dock.' 
    },
    { 
      icon: Keyboard, 
      title: 'Keyboard Shortcut', 
      desc: 'Trigger the color picker from anywhere with a global hotkey. The ultimate power user tool.' 
    }
  ];

  return (
    <div data-testid="coluro-page" className="bg-[#0F0F0F] text-white">
      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-32 pb-20 hero-gradient grid-pattern overflow-hidden">
        {/* Blue Glow for Coluro Identity */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#af0000]/10 blur-[120px] rounded-full" />
        
        <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="coluro-hero-content">
            <div className="inline-flex items-center gap-2 bg-[#af0000]/20 text-[#af0000] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Zap size={16} />
              Minimal. Precise. Fast.
            </div>
            
            <div className="flex items-center gap-4 mb-8">
               <img 
                 src="/coluro/Coluro-iOS-Default-1024x1024@1x.png" 
                 alt="Coluro Icon" 
                 className="w-16 h-16 rounded-2xl shadow-2xl"
               />
               <h1 className="text-4xl md:text-5xl font-bold font-['Syne'] tracking-tight">Coluro</h1>
            </div>

            <h2 className="text-5xl md:text-7xl font-bold font-['Syne'] tracking-tighter leading-[0.9] mb-8">
              Pick any color.<br />
              <span className="text-[#af0000]">Instantly.</span>
            </h2>
            
            <p className="text-xl md:text-2xl text-[#94a3b8] font-light max-w-xl mb-10 leading-relaxed">
              The fastest color picker for Mac — built for designers and developers who value precision and speed.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <button className="bg-[#af0000] hover:bg-[#8a0000] text-white px-8 py-4 rounded-full font-bold transition-all hover:scale-105 flex items-center justify-center gap-2">
                Download Free
                <ArrowRight size={18} />
              </button>
              <button 
                onClick={() => setIsSupportModalOpen(true)}
                className="btn-secondary inline-flex items-center justify-center gap-2"
              >
                Contact Support
                <ArrowUpRight size={18} />
              </button>
            </div>

            <div className="mt-12 flex items-center gap-3 text-[#64748b] text-sm">
               <Monitor size={16} />
               <span>macOS 13+ • Apple Silicon & Intel</span>
            </div>
          </div>
          
          <div className="coluro-hero-visual relative">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-[#af0000]/5">
              <img 
                src="/coluro/screenshot-1.jpg" 
                alt="Coluro App Screenshot" 
                className="w-full h-auto"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-32 h-32 border-2 border-[#af0000]/30 -z-10 rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section ref={featuresRef} id="features" className="py-24 md:py-32 coluro-section border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-20 text-center lg:text-left">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Features</p>
            <h2 className="text-4xl md:text-6xl font-bold font-['Syne'] mb-6">Built for <span className="text-[#af0000]">precision.</span></h2>
            <p className="text-[#94a3b8] text-lg max-w-2xl mx-auto lg:mx-0">Everything you need in a color picker, nothing you don't. Optimized for your existing workflow.</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 features-grid">
            {features.map((f, i) => (
              <div key={i} className="feature-card group p-8 bg-white/5 hover:bg-white/10 border border-white/10 transition-all duration-500 rounded-2xl">
                <div className="w-12 h-12 bg-[#af0000]/10 flex items-center justify-center rounded-xl mb-6 group-hover:bg-[#af0000]/20 transition-colors">
                  <f.icon size={24} className="text-[#af0000]" />
                </div>
                <h3 className="text-xl font-bold font-['Syne'] mb-3">{f.title}</h3>
                <p className="text-[#94a3b8] text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 md:py-32 bg-[#141414] coluro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
             <div className="order-2 lg:order-1">
                <div className="rounded-2xl overflow-hidden border border-white/10">
                   <img src="/coluro/screenshot-2.jpg" alt="Coluro in action" className="w-full h-auto" />
                </div>
             </div>
             <div className="order-1 lg:order-2">
                <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Workflow</p>
                <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] mb-12">How it works</h2>
                
                <div className="space-y-12">
                   <Step num="01" title="Trigger" desc="Press your hotkey or click the icon in your menu bar to activate the picker." />
                   <Step num="02" title="Pick" desc="Hover over any pixel on your screen to see its value in a magnified preview." />
                   <Step num="03" title="Copy" desc="Click to copy the value instantly in HEX, RGB, HSL, or HSB format." />
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section ref={pricingRef} id="pricing" className="py-24 md:py-32 coluro-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-20">
            <p className="text-[#af0000] font-mono text-sm uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="text-4xl md:text-5xl font-bold font-['Syne']">Simple, <span className="text-[#af0000]">transparent</span> pricing.</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
             {/* Free Tier */}
             <div className="p-10 bg-white/5 border border-white/10 rounded-3xl flex flex-col">
                <h3 className="text-2xl font-bold font-['Syne'] mb-2">FREE</h3>
                <div className="text-4xl font-bold mb-8">$0 <span className="text-sm font-normal text-[#64748b]">forever</span></div>
                <ul className="space-y-4 mb-10 flex-grow text-[#94a3b8]">
                   <PriceFeature text="Instant Color picker" />
                   <PriceFeature text="HEX, RGB, HSL, HSB support" />
                   <PriceFeature text="Save up to 3 palettes" />
                   <PriceFeature text="Contrast checker" />
                </ul>
                <button className="w-full btn-secondary py-4">Download Now</button>
             </div>

             {/* Pro Tier */}
             <div className="p-10 bg-white/5 border-2 border-[#af0000] rounded-3xl flex flex-col relative overflow-hidden">
                <div className="absolute top-4 right-4 bg-[#af0000] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-tighter">Popular</div>
                <h3 className="text-2xl font-bold font-['Syne'] mb-2">PRO</h3>
                <div className="text-4xl font-bold mb-8">$4.99 <span className="text-sm font-normal text-[#64748b]">one-time</span></div>
                <ul className="space-y-4 mb-10 flex-grow text-[#f8fafc]">
                   <PriceFeature text="Everything in Free" active />
                   <PriceFeature text="Unlimited saved palettes" active />
                   <PriceFeature text="Advanced export options" active />
                   <PriceFeature text="All future Pro features" active />
                   <PriceFeature text="Never a subscription" active />
                </ul>
                <button className="w-full bg-[#af0000] hover:bg-[#8a0000] text-white py-4 rounded-full font-bold transition-all hover:scale-105">Get Pro Now</button>
             </div>
          </div>
        </div>
      </section>

      {/* Support Section */}
      <section className="py-24 md:py-32 coluro-section border-t border-white/10 bg-white/[0.02]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
           <h2 className="text-4xl md:text-5xl font-bold font-['Syne'] mb-6">Need help?</h2>
           <p className="text-[#94a3b8] text-lg mb-10">Our team is here to assist you with any questions or technical issues.</p>
           <button 
             onClick={() => setIsSupportModalOpen(true)}
             className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 px-10 py-5 rounded-full font-bold transition-all"
           >
             Open Support Portal
             <Mail size={20} className="text-[#af0000]" />
           </button>
        </div>
      </section>

      {/* Support Modal */}
      {isSupportModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setIsSupportModalOpen(false)}
          />
          <div className="relative bg-[#1A1A1A] border border-white/10 rounded-3xl p-8 max-w-lg w-full shadow-2xl overflow-hidden">
            <button 
              onClick={() => setIsSupportModalOpen(false)}
              className="absolute top-6 right-6 text-[#64748b] hover:text-white transition-colors"
            >
              <X size={24} />
            </button>
            
            <h3 className="text-3xl font-bold font-['Syne'] mb-2">Support Portal</h3>
            <p className="text-[#94a3b8] mb-8">Copy the details below to send us an email.</p>

            <div className="space-y-6">
               <CopyField 
                 label="Email Address" 
                 value={supportInfo.email} 
                 onCopy={() => copyToClipboard(supportInfo.email, 'email')} 
                 isCopied={copiedField === 'email'} 
               />
               <CopyField 
                 label="Subject" 
                 value={supportInfo.subject} 
                 onCopy={() => copyToClipboard(supportInfo.subject, 'subject')} 
                 isCopied={copiedField === 'subject'} 
               />
               <div className="space-y-2">
                 <label className="text-xs uppercase tracking-widest text-[#64748b] font-bold">Email Body</label>
                 <div className="relative group">
                    <div className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-[#94a3b8] min-h-[100px] leading-relaxed">
                      {supportInfo.body}
                    </div>
                    <button 
                      onClick={() => copyToClipboard(supportInfo.body, 'body')}
                      className="absolute top-3 right-3 p-2 bg-white/10 hover:bg-[#af0000] text-white rounded-lg transition-all opacity-0 group-hover:opacity-100"
                    >
                      {copiedField === 'body' ? <CheckCircle size={16} /> : <Copy size={16} />}
                    </button>
                 </div>
               </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CopyField = ({ label, value, onCopy, isCopied }) => (
  <div className="space-y-2">
    <label className="text-xs uppercase tracking-widest text-[#64748b] font-bold">{label}</label>
    <div className="relative flex items-center">
       <div className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-[#f8fafc] font-mono text-sm truncate pr-14">
         {value}
       </div>
       <button 
         onClick={onCopy}
         className="absolute right-2 p-3 text-[#64748b] hover:text-[#af0000] transition-colors"
       >
         {isCopied ? <CheckCircle size={18} className="text-[#10B981]" /> : <Copy size={18} />}
       </button>
    </div>
  </div>
);

const Step = ({ num, title, desc }) => (
  <div className="step-item flex gap-6">
    <div className="text-3xl font-bold font-['Syne'] text-[#af0000]/30">{num}</div>
    <div>
      <h3 className="text-xl font-bold font-['Syne'] mb-2">{title}</h3>
      <p className="text-[#94a3b8] leading-relaxed">{desc}</p>
    </div>
  </div>
);

const PriceFeature = ({ text, active }) => (
  <li className="flex items-center gap-3">
    <CheckCircle size={18} className={active ? "text-[#af0000]" : "text-[#64748b]"} />
    <span>{text}</span>
  </li>
);

export default Coluro;
